import { useCallback, useMemo, useReducer, useRef, useEffect } from 'react';
import {
  ActivityIndicator,
  Animated,
  Platform,
  Pressable,
  StyleSheet,
  Text,
  View,
  type StyleProp,
} from 'react-native';
import { Image, type ImageStyle } from 'expo-image';
import { useTheme, SPACING, BORDER_RADIUS, TYPOGRAPHY, ON_COLOR } from '../../../theme';
import type { ThemeColors } from '../../../theme/colors';
import { CURSOR_POINTER } from '../../../utils/webStyles';
import { displayUrlsFor, type DeckItem, type Slide, type SlideImage } from './deck';

/**
 * One deck item, drawn into a stage of a fixed size.
 *
 * Two rules hold this component together, both learned from the blank-white
 * gap this replaced:
 *
 * 1. The ground is always dark and always painted (colors.presentationStage,
 *    dark in both themes). Nothing shows the panel's light surface through a
 *    slide, however slowly the photo arrives.
 * 2. The stage's height never depends on what is on it. In the panel layout
 *    it is a fixed box, so a title card and a photo slide are the same size
 *    and the controls under them never move.
 *
 * Photos paint in three steps: the blurred backdrop and the sharp image both
 * start from the 400px thumbnail, the ~500KB display file fades in over it on
 * load, and a spinner sits in the middle until it does. A URL that has
 * loaded once in this session is remembered module-wide, so stepping back to
 * an earlier slide shows no spinner at all.
 */

const BACKDROP_BLUR = 40;
// Placeholder -> display crossfade inside one image, in ms. Short: the
// thumbnail is already the right picture, only sharper afterwards.
const IMAGE_TRANSITION_MS = 200;
// Item -> item crossfade, in ms.
const ITEM_FADE_MS = 250;

// Panel-only sizing. The DockedPanel's ScrollView gives a flex child no
// height to fill, so the panel stage is a fixed box; the info strip inside
// it is fixed too, which keeps the photo area itself the same size on a
// slide with a caption and one without.
const PANEL_STAGE_HEIGHT = 520;
const PANEL_INFO_HEIGHT = 112;

/** Display URLs that have finished loading at least once this session.
 *  Module-level on purpose: it must outlive the stage, which unmounts every
 *  time the presentation moves between the panel and fullscreen. */
const loadedUrls = new Set<string>();

type StageLayout = 'panel' | 'fullscreen';

interface SlideStageProps {
  item: DeckItem | undefined;
  layout: StageLayout;
  /** "3 of 14", or undefined on a title card and the empty state. */
  counterText?: string;
  onPrev: () => void;
  onNext: () => void;
  onAddressPress: (slide: Slide) => void;
  /** 'previous' is the outgoing layer of PresentationCrossfade's two-layer
   *  crossfade: every testID gets a `-previous` suffix and the root is
   *  aria-hidden and inert, so audits and a11y tools querying by testID
   *  still find exactly one "current" slide even while both are mounted.
   *  Defaults to 'current', which is every call site except that one. */
  variant?: 'current' | 'previous';
  /** Ken Burns zoom on the single-photo frame only (never the pair
   *  comparison layout, the blurred backdrop, or a title card). Driven by
   *  slideshowMotionCss.ts; the caller is responsible for turning it off
   *  under reduced motion. */
  zoom?: boolean;
  /** Skips the internal item->item opacity fade below. Set by
   *  PresentationCrossfade, which owns the fade at a higher level (a CSS
   *  transition, not this component's `Animated`) — without this, each
   *  layer would additionally re-run its own JS-driven fade on top of that,
   *  which is exactly the un-freezable-by-the-visual-audit motion this
   *  component's replacement was meant to avoid. PresentationPanel doesn't
   *  pass it, so its existing per-item fade is unchanged. */
  disableItemFade?: boolean;
}

export function SlideStage({
  item,
  layout,
  counterText,
  onPrev,
  onNext,
  onAddressPress,
  variant = 'current',
  zoom = false,
  disableItemFade = false,
}: SlideStageProps) {
  const { colors } = useTheme();
  const isPanel = layout === 'panel';
  const isPrevious = variant === 'previous';
  const tid = useCallback((base: string) => (isPrevious ? `${base}-previous` : base), [isPrevious]);

  // Every display URL this item renders — none for a title card.
  const urls = useMemo(() => (item ? displayUrlsFor(item) : []), [item]);
  const [, onImageLoaded] = useReducer((n: number) => n + 1, 0);
  const markLoaded = useCallback(
    (url: string) => {
      loadedUrls.add(url);
      onImageLoaded();
    },
    [onImageLoaded]
  );
  const waiting = urls.length > 0 && !urls.every((url) => loadedUrls.has(url));

  // Item -> item crossfade. Keyed on the deck item's identity (the deck is
  // memoized upstream, so the object is stable while the slide is up).
  // Skipped entirely when a wrapper (PresentationCrossfade) owns the fade.
  const fade = useRef(new Animated.Value(1)).current;
  const previousItem = useRef(item);
  useEffect(() => {
    if (disableItemFade) return;
    if (previousItem.current === item) return;
    previousItem.current = item;
    fade.setValue(0);
    Animated.timing(fade, {
      toValue: 1,
      duration: ITEM_FADE_MS,
      useNativeDriver: Platform.OS !== 'web',
    }).start();
  }, [item, fade, disableItemFade]);

  return (
    <View
      testID={tid('map-slideshow-stage')}
      aria-hidden={isPrevious || undefined}
      pointerEvents={isPrevious ? 'none' : undefined}
      style={[
        styles.root,
        isPanel ? styles.rootPanel : styles.rootFullscreen,
        { backgroundColor: colors.presentationStage },
      ]}>
      {counterText ? (
        <View style={[styles.counterChip, { backgroundColor: colors.overlay }]}>
          <Text
            style={[styles.counter, { color: ON_COLOR.textSecondary }]}
            testID={tid('map-slideshow-counter')}>
            {counterText}
          </Text>
        </View>
      ) : null}

      <Animated.View style={[styles.fadeLayer, disableItemFade ? undefined : { opacity: fade }]}>
        {!item ? (
          <View style={styles.emptyState}>
            <Text style={[styles.emptyText, { color: ON_COLOR.textSecondary }]}>
              Zoom in until observations are visible on the map.
            </Text>
          </View>
        ) : item.kind === 'title' ? (
          <View
            testID={tid('map-slideshow-title')}
            style={[
              styles.titleCard,
              {
                backgroundColor:
                  item.category === 'progress'
                    ? colors.mapMarkerProgress
                    : colors.mapMarkerOpportunity,
              },
            ]}>
            <Text
              style={[
                isPanel ? styles.titleHeadlinePanel : styles.titleHeadline,
                { color: ON_COLOR.text },
              ]}>
              {item.category === 'progress'
                ? 'Progress in this area'
                : 'Opportunities in this area'}
            </Text>
            <Text
              style={[
                isPanel ? styles.titleCountPanel : styles.titleCount,
                { color: ON_COLOR.textSecondary },
              ]}>
              {item.count} {item.count === 1 ? 'observation' : 'observations'}
            </Text>
          </View>
        ) : (
          <>
            <View style={styles.photoArea} testID={tid('map-slideshow-slide')}>
              <Pressable
                onPress={onPrev}
                accessible={false}
                style={styles.leftZone}
                testID={tid('map-slideshow-left-zone')}
              />
              <Pressable
                onPress={onNext}
                accessible={false}
                style={styles.rightZone}
                testID={tid('map-slideshow-right-zone')}
              />
              {item.beforeImage ? (
                <View pointerEvents="none" style={styles.pairRow}>
                  <View style={styles.pairHalf}>
                    <StageImage image={item.beforeImage} onLoaded={markLoaded} colors={colors} />
                    <View style={[styles.pairLabel, { backgroundColor: colors.overlay }]}>
                      <Text style={[styles.pairLabelText, { color: ON_COLOR.text }]}>Before</Text>
                    </View>
                  </View>
                  <View style={styles.pairHalf}>
                    <StageImage image={item.image} onLoaded={markLoaded} colors={colors} />
                    <View style={[styles.pairLabel, { backgroundColor: colors.overlay }]}>
                      <Text style={[styles.pairLabelText, { color: ON_COLOR.text }]}>After</Text>
                    </View>
                  </View>
                </View>
              ) : (
                <View pointerEvents="none" style={styles.singleFrame}>
                  <StageImage
                    image={item.image}
                    onLoaded={markLoaded}
                    colors={colors}
                    photoStyle={styles.photoSingle}
                    zoom={zoom}
                  />
                </View>
              )}
              {waiting ? (
                <View pointerEvents="none" style={styles.spinner}>
                  <ActivityIndicator testID={tid('map-slideshow-loading')} color={ON_COLOR.text} />
                </View>
              ) : null}
            </View>

            <View style={isPanel ? styles.infoBarPanel : styles.infoBar}>
              <View
                style={[
                  styles.chip,
                  {
                    backgroundColor:
                      item.category === 'progress'
                        ? colors.mapMarkerProgress
                        : colors.mapMarkerOpportunity,
                  },
                ]}>
                <Text style={[styles.chipText, { color: ON_COLOR.text }]}>
                  {item.category === 'progress' ? 'Progress' : 'Opportunity'}
                </Text>
              </View>
              {item.caption ? (
                <Text style={[styles.caption, { color: ON_COLOR.text }]} numberOfLines={2}>
                  {item.caption}
                </Text>
              ) : null}
              {item.addressText ? (
                <Pressable
                  onPress={() => onAddressPress(item)}
                  testID={tid('map-slideshow-address')}
                  accessibilityRole="button"
                  accessibilityLabel={`Center the map on ${item.addressText}`}
                  style={CURSOR_POINTER}>
                  <Text
                    style={[
                      isPanel ? styles.addressPanel : styles.address,
                      { color: ON_COLOR.text },
                    ]}
                    numberOfLines={1}>
                    {item.addressText}
                  </Text>
                </Pressable>
              ) : null}
            </View>
          </>
        )}
      </Animated.View>
    </View>
  );
}

/**
 * One photo, drawn as a progressive pair: the thumbnail carries both the
 * blurred full-bleed backdrop (so a portrait phone photo fills the frame
 * instead of leaving bands beside it) and the sharp image's placeholder,
 * and the display file fades in over it. The backdrop is scaled past its own
 * edges so the blur doesn't feather to transparency at the container border
 * (same trick as PostCard's detail-view backdrop).
 */
function StageImage({
  image,
  onLoaded,
  colors,
  photoStyle,
  zoom,
}: {
  image: SlideImage;
  onLoaded: (url: string) => void;
  colors: ThemeColors;
  photoStyle?: StyleProp<ImageStyle>;
  /** Ken Burns zoom, applied to this wrapping frame (never the blurred
   *  backdrop below) so the sharp photo alone slowly scales up. */
  zoom?: boolean;
}) {
  const first = image.thumbnail ?? image.display;
  const placeholder = image.thumbnail ? { uri: image.thumbnail } : undefined;
  return (
    <>
      <View style={styles.backdropContainer} pointerEvents="none">
        <Image
          source={{ uri: first }}
          style={styles.backdropImage}
          contentFit="cover"
          blurRadius={BACKDROP_BLUR}
        />
        <View style={[styles.backdropOverlay, { backgroundColor: colors.black }]} />
      </View>
      <View
        style={photoStyle ?? styles.photo}
        // @ts-expect-error rediAnim is a web-only DOM dataset hook picked up
        // by slideshowMotionCss.ts's Ken Burns keyframe; ignored on native.
        dataSet={zoom ? { rediAnim: 'slideshow-kenburns' } : undefined}>
        <Image
          source={{ uri: image.display }}
          placeholder={placeholder}
          placeholderContentFit="contain"
          contentFit="contain"
          transition={IMAGE_TRANSITION_MS}
          onLoad={() => onLoaded(image.display)}
          style={styles.fill}
          pointerEvents="none"
        />
      </View>
    </>
  );
}

const styles = StyleSheet.create({
  root: {
    width: '100%',
    position: 'relative',
    overflow: 'hidden',
  },
  rootPanel: {
    height: PANEL_STAGE_HEIGHT,
    borderRadius: BORDER_RADIUS.md,
  },
  rootFullscreen: {
    flex: 1,
  },
  fadeLayer: {
    flex: 1,
    width: '100%',
    alignItems: 'center',
    justifyContent: 'center',
  },
  counterChip: {
    position: 'absolute',
    top: SPACING.md,
    left: SPACING.md,
    paddingHorizontal: SPACING.sm,
    paddingVertical: SPACING.xxs,
    borderRadius: BORDER_RADIUS.sm,
    zIndex: 1,
  },
  counter: {
    ...TYPOGRAPHY.caption,
  },
  emptyState: {
    paddingHorizontal: SPACING.xl,
    alignItems: 'center',
  },
  emptyText: {
    ...TYPOGRAPHY.body,
    textAlign: 'center',
  },
  titleCard: {
    flex: 1,
    width: '100%',
    alignItems: 'center',
    justifyContent: 'center',
    paddingHorizontal: SPACING.xl,
  },
  titleHeadline: {
    ...TYPOGRAPHY.presentationTitle,
    textAlign: 'center',
    marginBottom: SPACING.sm,
  },
  titleHeadlinePanel: {
    ...TYPOGRAPHY.h2,
    textAlign: 'center',
    marginBottom: SPACING.xs,
  },
  titleCount: {
    ...TYPOGRAPHY.h3,
    textAlign: 'center',
  },
  titleCountPanel: {
    ...TYPOGRAPHY.body,
    textAlign: 'center',
  },
  photoArea: {
    flex: 1,
    width: '100%',
    alignItems: 'center',
    justifyContent: 'center',
    position: 'relative',
  },
  leftZone: {
    position: 'absolute',
    left: 0,
    top: 0,
    bottom: 0,
    width: '35%',
  },
  rightZone: {
    position: 'absolute',
    right: 0,
    top: 0,
    bottom: 0,
    width: '35%',
  },
  spinner: {
    ...StyleSheet.absoluteFillObject,
    alignItems: 'center',
    justifyContent: 'center',
  },
  // Full-bleed: the blurred backdrop spans the whole stage, the sharp photo
  // sits centered at 92% of it.
  singleFrame: {
    ...StyleSheet.absoluteFillObject,
    alignItems: 'center',
    justifyContent: 'center',
  },
  photoSingle: {
    width: '92%',
    height: '100%',
  },
  // Clipped: the blurred image is scaled past its box to hide the blur's
  // soft edges, and without overflow hidden that scaled bleed showed below
  // the photo area as an undarkened band across the info strip.
  backdropContainer: {
    ...StyleSheet.absoluteFillObject,
    overflow: 'hidden',
  },
  backdropImage: {
    ...StyleSheet.absoluteFillObject,
    transform: [{ scale: 1.15 }],
  },
  backdropOverlay: {
    ...StyleSheet.absoluteFillObject,
    opacity: 0.45,
  },
  photo: {
    width: '100%',
    height: '100%',
  },
  // The Ken Burns keyframe (slideshowMotionCss.ts) scales this frame, not
  // the Image inside it, which always just fills whatever frame it's given.
  fill: {
    width: '100%',
    height: '100%',
  },
  pairRow: {
    flexDirection: 'row',
    width: '95%',
    height: '100%',
    gap: SPACING.sm,
  },
  pairHalf: {
    flex: 1,
    position: 'relative',
  },
  pairLabel: {
    position: 'absolute',
    bottom: SPACING.sm,
    left: SPACING.sm,
    paddingHorizontal: SPACING.sm,
    paddingVertical: SPACING.xxs,
    borderRadius: BORDER_RADIUS.sm,
  },
  pairLabelText: {
    ...TYPOGRAPHY.caption,
  },
  infoBar: {
    width: '100%',
    maxWidth: 900,
    paddingHorizontal: SPACING.xl,
    paddingBottom: SPACING.sm,
    alignItems: 'center',
  },
  infoBarPanel: {
    height: PANEL_INFO_HEIGHT,
    width: '100%',
    paddingHorizontal: SPACING.md,
    alignItems: 'center',
    justifyContent: 'center',
  },
  chip: {
    paddingHorizontal: SPACING.smd,
    paddingVertical: SPACING.xs,
    borderRadius: BORDER_RADIUS.sm,
    marginBottom: SPACING.sm,
  },
  chipText: {
    ...TYPOGRAPHY.button,
  },
  caption: {
    ...TYPOGRAPHY.body,
    textAlign: 'center',
    marginBottom: SPACING.xs,
  },
  address: {
    ...TYPOGRAPHY.presentationSubtitle,
    textAlign: 'center',
  },
  addressPanel: {
    ...TYPOGRAPHY.h3,
    textAlign: 'center',
  },
});
